/**
 * Temperature and buying power for the Workspace list's rows. The rows mix
 * Accounts, and the CRM answers per Account (the Chats page's own list asks
 * the same routes: chats/useEnrichment.ts). So the page's first draw never
 * waits on them:
 * - a row asks only once it comes near the screen;
 * - asks wait a moment, then go out as one batch per Account and route;
 * - an answer redraws only the rows that show it, not the page;
 * - answers are kept for a while, so switching tabs asks nothing again.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { crmFetch, dataOf } from '../chat/api';
import { chatKey } from './chats';

export interface FanExtra {
  /** 0–100; null: the CRM has none. Absent: not answered yet. */
  temperature?: number | null;
  /** OnlyMonster's raw value; null: the CRM has none. Absent: not answered yet. */
  buyingPower?: unknown;
}

type Field = 'temperature' | 'buyingPower';
type Item = Record<string, unknown>;

const SOURCES: Record<Field, { path: string; pick: (it: Item) => unknown }> = {
  temperature: { path: '/v1/fan-insights/batch', pick: (it) => (it.temperature == null ? null : Number(it.temperature)) },
  buyingPower: { path: '/v1/buying-power/batch', pick: (it) => it.buying_power ?? null },
};
const CHUNK = 100;
/** Asks gather this long, so a screenful of rows is one request per Account. */
const GATHER_MS = 250;
/** An answer older than this is asked again when its row is drawn again. */
const STALE_MS = 10 * 60_000;

const values = new Map<string, FanExtra>();
const askedAt: Record<Field, Map<string, number>> = { temperature: new Map(), buyingPower: new Map() };
const queue: Record<Field, Map<string, Set<string>>> = { temperature: new Map(), buyingPower: new Map() };
const listeners = new Map<string, Set<() => void>>();
let timer: ReturnType<typeof setTimeout> | null = null;

function settle(accountId: string, field: Field, fanIds: string[], items: Item[]) {
  const got = new Map(items.filter((it) => it?.fan_id != null).map((it) => [String(it.fan_id), SOURCES[field].pick(it)]));
  for (const fanId of fanIds) {
    const key = chatKey(accountId, fanId);
    // A new object per answer: rows read it as their snapshot.
    values.set(key, { ...values.get(key), [field]: got.has(fanId) ? got.get(fanId) : null });
    listeners.get(key)?.forEach((fn) => fn());
  }
}

function flush() {
  timer = null;
  for (const field of Object.keys(queue) as Field[]) {
    for (const [accountId, ids] of queue[field]) {
      const fanIds = [...ids];
      for (let i = 0; i < fanIds.length; i += CHUNK) {
        const chunk = fanIds.slice(i, i + CHUNK);
        crmFetch(SOURCES[field].path, { accountId, local: true, body: { fan_ids: chunk } })
          .then((body) => settle(accountId, field, chunk, dataOf<{ items?: Item[] }>(body)?.items ?? []))
          // Not answered: a later draw of the row asks again.
          .catch(() => chunk.forEach((id) => askedAt[field].delete(chatKey(accountId, id))));
      }
    }
    queue[field].clear();
  }
}

function ask(accountId: string, fanId: string, fields: Field[]) {
  // Only real OnlyFans fan ids (the CRM rejects the rest).
  if (!accountId || !/^[1-9]\d*$/.test(fanId)) return;
  const key = chatKey(accountId, fanId);
  const now = Date.now();
  for (const field of fields) {
    const at = askedAt[field].get(key);
    if (at != null && now - at < STALE_MS) continue;
    askedAt[field].set(key, now);
    const ids = queue[field].get(accountId) ?? new Set<string>();
    ids.add(fanId);
    queue[field].set(accountId, ids);
    timer ??= setTimeout(flush, GATHER_MS);
  }
}

function subscribe(key: string, fn: () => void): () => void {
  const set = listeners.get(key) ?? new Set();
  set.add(fn);
  listeners.set(key, set);
  return () => {
    set.delete(fn);
    if (!set.size) listeners.delete(key);
  };
}

/**
 * What is known about one row's fan. With `ask`, it is fetched (batched with
 * the other rows) when not known yet; buying power only with `buyingPower`
 * (the person can switch it off in Styling).
 */
export function useFanExtra(accountId: string, fanId: string, opts: { ask: boolean; buyingPower: boolean }): FanExtra | undefined {
  const key = chatKey(accountId, fanId);
  const sub = useCallback((fn: () => void) => subscribe(key, fn), [key]);
  const extra = useSyncExternalStore(sub, () => values.get(key));
  useEffect(() => {
    if (opts.ask) ask(accountId, fanId, opts.buyingPower ? ['temperature', 'buyingPower'] : ['temperature']);
  }, [accountId, fanId, opts.ask, opts.buyingPower]);
  return extra;
}

/** Tests: forget everything. */
export function resetFanExtras() {
  if (timer) clearTimeout(timer);
  timer = null;
  values.clear();
  listeners.clear();
  for (const field of Object.keys(queue) as Field[]) {
    queue[field].clear();
    askedAt[field].clear();
  }
}
